// Core
import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  Box,
  Flex,
  Text,
  useColorModeValue,
  useDisclosure
} from '@chakra-ui/react';

// Accets
import { apiService } from 'services/ApiService';
import { getInitials, parseDateTime, validateLoadMore } from 'services/Utils';
import { ReactComponent as TabletIcon } from 'assets/svg/sessions/tablet.svg';
import { ReactComponent as DeleteIcon } from 'assets/svg/sessions/delete.svg';

// Components
import Card from 'components/Card/Card';
import Attention from 'components/Modals/AttentionModal';

const SessionItem = ({item, refetch, offset, setOffset}) => {

	const border = useColorModeValue('1px solid #CBE7FF', '1px solid #262D47');

	const { isOpen, onClose, onOpen } = useDisclosure();
	const attentionModal = async () => {
	    onOpen();
	};

	const handleDelete = async (item) => {
	    try {
	      	await apiService.deleteUserSession(item);
	        if(offset !== 0 ) { 
	        	setOffset(0); 
	        } else {
	        	refetch();	
	        }
	    } catch (error) {}
	};

	return (
		<Flex pt="12px" gap="20px" alignItems="start" w="100%">
          <Box p="4px" w="32px" h="32px" borderRadius="8px" bg="rgba(9, 155, 255, 0.10)" position="relative">
            <TabletIcon fontSize="24px" />
          </Box>

          <Flex 
          	borderBottom={ item?.current ? 'unset' : border }  
          	pb={ item?.current ? 'unset' : '12px' }  
          	w="100%" 
          	justifyContent="space-between">
          	<Box>

	            <Text 
	            	variant="title-md" 
	            	fontWeight="700" 
	            	textTransform="capitalize"
	            	mb="3px">
	            	{item?.platform?.os?.name ?? 'Unknown'}
	            </Text>

	            <Flex py="4px" gap="4px" alignItems="end">
	            	<Text fontSize="13px">App:</Text>
	            	<Text fontSize="15px">{item?.platform?.client?.version}</Text>
	            </Flex>

	            <Flex py="4px" gap="4px" alignItems="end">
	            	<Text fontSize="13px">Location:</Text>
	            	<Text fontSize="15px">{item?.location?.countryName}, {item?.location?.cityName}</Text>
	            </Flex>

	            <Flex py="4px" gap="4px" alignItems="end">
	            	<Text fontSize="13px">IP:</Text>
	            	<Text fontSize="15px">{item?.location?.ip}</Text>
	            </Flex>

	            <Flex py="4px" gap="4px" alignItems="end">
	            	<Text fontSize="13px">Activity:</Text>
	            	<Text
	            		color={item?.current ? '#34C759' : 'inherit'} 
	            		fontSize="15px">{item?.current ? 'Online' : parseDateTime(item?.lastLogin) }</Text>
	            </Flex>

            </Box>
          </Flex>

          <Box
          	opacity={ item?.current ? '0.2' : '1' } 
          	pointerEvents={ item?.current ? 'none' : 'unset' }>
          	<DeleteIcon onClick={attentionModal} cursor="pointer" fontSize="24px" />
          </Box>

          <Attention
	        handle={() => handleDelete(item)}
	        isOpen={isOpen}
	        onClose={onClose}
	        title="Finalize the device"
	        description="Exit on device except the current device"
	      />

        </Flex>
	);
};

const Sessions = ({ userData }) => {
  const textColor = useColorModeValue('gray.700', '#9FABCB');

  const bg = useColorModeValue('white', '#0E1633');
  const border = useColorModeValue('1px solid #CBE7FF', '1px solid #262D47');
  const boxShadow = useColorModeValue('unset', '0px 1px 3px 0px rgba(0, 0, 0, 0.10),0px 1px 2px 0px rgba(0, 0, 0, 0.06)');

  const limit = 5;
  const [currentItem, setCurrentItem] = useState();
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState([]);
  const [dataMeta, setDataMeta] = useState(); 
  const { isLoading, refetch } = useQuery({
      queryKey: ['sessions', offset],
      queryFn: async () => {
        if(!localStorage.getItem('auth')) { return true; }

        const response = await apiService.getUserSessions({ limit, offset });
        if(offset === 0){
            setData(Array.isArray(response.data) ? response.data.slice(1) : []);
            setCurrentItem(response?.data[0] ?? {});
        } else {
            setData([...data, ...response.data]);
        }
        setDataMeta(response.meta);
        return true;
      },
      refetchOnWindowFocus: false
  });

  const loadMore = async () => {
      setOffset(offset + limit);
  };

  const { isOpen, onClose, onOpen } = useDisclosure();
  const attentionModal = async () => {
    onOpen();
  };

  const handleDeleteAll = async () => {
	  try {
      	await apiService.deleteUserSessions();
		refetch();	
	  } catch (error) {}
  };

  return (
  	<>
  		<Text mb="8px" color="#9FABCB" fontSize="13px" fontStyle="normal" fontWeight="500" lineHeight="18px" letterSpacing="-0.4px" textTransform="uppercase">
  			This device
  		</Text>
  		<Card padding="0px 12px 12px 12px" borderRadius="12px" bg={bg} boxShadow={boxShadow}>	    
		    <SessionItem item={currentItem} />
	    </Card>

  		<Button my="16px" variant="button-lg" disabled={ data?.length > 0 ? false : true} onClick={attentionModal}>
  			Stop All
  		</Button>

  		 {data?.length > 0 && (
  		 	<>
		  		<Text mb="8px" color="#9FABCB" fontSize="13px" fontStyle="normal" fontWeight="500" lineHeight="18px" letterSpacing="-0.4px" textTransform="uppercase">
		  			Active sessions
		  		</Text>

			    <Card padding="0px 12px 12px 12px" borderRadius="12px" bg={bg} boxShadow={boxShadow}>	    
				    { data.map((item, index) => (
				        <SessionItem key={index} item={item} refetch={refetch} setOffset={setOffset} offset={offset}/>
				    ))}

				    { validateLoadMore(data, dataMeta) && (
				        <Button variant="ghost-md" maxW="220px" onClick={loadMore} m="24px auto !important" isLoading={isLoading}>
				          Load More
				        </Button>
				    )}
			    </Card>
		    </>
	    )}

	    <Attention
	        handle={handleDeleteAll}
	        isOpen={isOpen}
	        onClose={onClose}
	        title="End other sessions"
	        description="Exit on all devices except the current device"
	    />
    </>
  );
};

export default Sessions;